import {AxiosInstance, AxiosResponse} from "axios";
import {ConsumerReconciliation, ReconciliationCandidate} from "@/core/interfaces/consumers/ConsumerReconciliation";
import {PaginatedData} from "@/core/interfaces/api";

interface ReconciliationKeyAction {
    /** The consumer key. */
    consumer_key: string;
    /** The related action. */
    action: "add" | "remove" | "reject";
}

interface ReconciliationUpdateRequest {
    /** The batched key actions, applied by the server in a single pass. */
    keys: ReconciliationKeyAction[];
}

export interface ReconciliationIndexRequest {
    /** An optional consumer key to filter index. */
    consumer_key?: string;
    /** Should we load sales. */
    with_sales?: boolean;
    /** The page number. */
    page?: number;
    /** The page size. */
    size?: number;
}

/**
 * The ASAFO Api resource.
 * @param {Object} client - The injected client.
 */
export default function (client: AxiosInstance) {
    return {
        /**
         * List reconciliation candidates.
         */
        async candidates(params: ReconciliationIndexRequest): Promise<PaginatedData<ReconciliationCandidate[]>> {
            return await client.get('consumer-reconciliations/candidates', {params})
                .then((response: AxiosResponse) => response.data);
        },
        /**
         * List reconciliations.
         */
        async index(params: ReconciliationIndexRequest): Promise<PaginatedData<ConsumerReconciliation[]>> {
            return await client.get('consumer-reconciliations', {params})
                .then((response: AxiosResponse) => response.data);
        },
        /**
         * Reconcile consumer keys.
         *
         * @param {ReconciliationUpdateRequest} request The reconciliation request.
         */
        async create(request: ReconciliationUpdateRequest): Promise<ConsumerReconciliation> {
            return await client.post('consumer-reconciliations', request)
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Update a consumer reconciliation.
         *
         * @param {number} id The reconciliation ID.
         * @param {ReconciliationUpdateRequest} request The reconciliation request.
         */
        async update(id: number, request: ReconciliationUpdateRequest): Promise<ConsumerReconciliation> {
            return await client.patch(`consumer-reconciliations/${id}`, request)
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Get a consumer reconciliation by ID.
         *
         * @param {number} id The reconciliation ID.
         */
        async read(id: number): Promise<ConsumerReconciliation> {
            return await client.get(`consumer-reconciliations/${id}`)
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Delete a consumer reconciliation.
         *
         * @param {number} id The reconciliation ID.
         */
        async delete(id: number): Promise<void> {
            return await client.delete(`consumer-reconciliations/${id}`);
        }
    };
}
